import * as React from 'react';
import type { EntityType } from '@modules/build-hub-comment/types';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { capitalizeFirstLetter } from '~/utils/stringHelper';
import { getInitials } from '~/modules/build-hub/utils/get-initials';
import { UserPlus } from 'lucide-react';

export interface Candidate {
    id: string;
    label: string;
    avatar: string | null;
    type: EntityType;
    onAction?: () => void; // Optional action handler for action-type candidates
}

interface Props {
    query: string;
    candidates: Candidate[];
    highlightIndex: number;
    onSelect: (candidate: Candidate) => void;
    onCancel: () => void;
}

export function CommentAutoComplete({
    candidates,
    highlightIndex,
    onSelect,
    onCancel,
}: Props) {
    if (candidates.length === 0) return null;

    return (
        <div className="absolute bottom-full z-10 mb-3 w-full overflow-hidden rounded-lg border bg-white shadow">
            {candidates.map((c, i) => {
                const isActive = i === highlightIndex;
                const isAction = c.type === 'action';

                return (
                    <div
                        key={`${c.type}:${c.id}`}
                        className={`flex cursor-pointer items-center gap-3 px-3 py-2 ${
                            isActive
                                ? 'bg-brick-100 text-white'
                                : 'hover:bg-gray-100'
                        } ${isAction ? 'border-t border-gray-200' : ''}`}
                        onClick={() => {
                            if (isAction && c.onAction) {
                                c.onAction();
                                onCancel();
                            } else {
                                onSelect(c);
                            }
                        }}
                        tabIndex={0}
                    >
                        {isAction ? (
                            <div
                                className={`flex h-10 w-10 items-center justify-center rounded ${
                                    isActive ? 'bg-white/20' : 'bg-gray-100'
                                }`}
                            >
                                <UserPlus
                                    className={`h-5 w-5 ${
                                        isActive
                                            ? 'text-white'
                                            : 'text-gray-600'
                                    }`}
                                />
                            </div>
                        ) : (
                            <Avatar size="square40">
                                <AvatarImage
                                    src={c.avatar ?? ''}
                                    alt={c.label}
                                />
                                <AvatarFallback size="md">
                                    {getInitials(c.label)}
                                </AvatarFallback>
                            </Avatar>
                        )}
                        <div className="flex flex-col">
                            <span className={isAction ? 'font-medium' : ''}>
                                {c.label}
                            </span>
                            {!isAction && (
                                <small
                                    className={
                                        isActive
                                            ? 'text-blue-700'
                                            : 'text-gray-400'
                                    }
                                >
                                    {capitalizeFirstLetter(c.type)}
                                </small>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
